export interface BankOfChangesOption {
  text: string;
  score: number;
  rank: number;
  original_extracted: string;
  method?: string;
  origin?: string;
  internal_note?: string;
}

export interface BankOfChangesMulti {
  chosen_option: string;
  options: BankOfChangesOption[];
}

export type BankOfChangesValue = string | BankOfChangesMulti | BankOfChangesOption[];

export interface CrossCheckInsight {
  exist_clause?: string;
  exist_clause_xml?: string;
  explanation: string;
  file_id?: string;
  paragraph_index?: number;
  original_xml?: string;
  edited_xml?: string;
  similarity_score?: number;
  suggested_xml?: string;
  chunk_metadata?: {
    file_id?: string;
    paragraph_index?: number;
    iteration_number?: number;
    author?: string;
    side?: string;
    timestamp?: string;
    filename?: string;
    similarity_score?: number;
  };
}

export interface ClauseSubmissionStatus {
  schedule_submitted: boolean;
  schedule_submitted_at?: string;
  response_submitted: boolean;
  response_submitted_at?: string;
}

export interface ClauseVerificationStatus {
  verified: boolean;
  verified_at?: string;
  verified_by?: string;
}

export interface ClauseLockStatus {
  locked: boolean;
  locked_at?: string;
  locked_by?: string;
}

export type CrossCheckDocumentType = 'sales_agreement' | 'appendix_a' | 'saved_for_later';

/**
 * Construction Loan-specific classification of a track-change request:
 *   - `commercial`: economic/financial terms, costs, pricing, payment terms.
 *   - `legal`:      contractual obligations, statutory compliance, remedies.
 *   - `other`:      neither commercial nor legal.
 *
 * MCC flows do not produce this field — they keep using `documentType` /
 * `refered_file` for grouping.
 */
export type CrossCheckMatter = 'commercial' | 'legal' | 'other';

export interface RawCrossCheckClause {
  id: string;
  clause_number?: string;
  documentType?: string;
  sales_agreement?: string;
  client_request?: string;
  sales_agreement_bank_of_changes?: BankOfChangesValue;
  appendix_a_bank_of_changes?: BankOfChangesValue;
  description?: string;
  color?: string;
  insights?: CrossCheckInsight[];
  submission_status?: ClauseSubmissionStatus;
  verification_status?: ClauseVerificationStatus;
  lock_status?: ClauseLockStatus;
  edit_history?: Record<string, Array<{ original_text: string; edited_text: string; edited_at: string; edited_by: string; edited_by_name?: string }>>;
  // Module B fields
  original_text?: string;
  edited_text?: string;
  track_changes?: any;
  participants?: string[];
  module_type?: 'module_a' | 'module_b';
  change_history?: any;
  side?: string;
  text?: string;
  refered_file?: string;
  /** Construction Loan only: matter classification used for UI tabs. */
  matter?: CrossCheckMatter | string;
  /** Optional paragraph index in the Word document (for replace/submit) */
  paragraph_index?: number;
  /** AI-generated interpretation of track changes (Module B); same as dashboard Explanation */
  track_changes_interpretation?: string;
  /** Original document type before a move (used to restore without re-analysis). */
  origin_document_type?: CrossCheckDocumentType;
  stale?: boolean;
  stale_reason?: 'moved' | 'reanalyzed';
}

export interface PastAgreementOption {
  id: string;
  /** Paragraph text — present in legacy payloads; resolved via playbook by-ids API in new payloads. */
  text?: string;
  /** Short LLM note: what addresses the client request vs not, and why (Construction Loan). */
  explanation?: string;
  /** Gemini similarity score 0-100: how well this past-contract clause addresses the client request. */
  similarity?: number;
}

export interface PastAgreementValue {
  options: PastAgreementOption[];
}

export interface CrossCheckClause {
  id: string;
  clause_number?: string;
  documentType: CrossCheckDocumentType;
  sales_agreement?: string;
  client_request?: string;
  bank_of_changes?: BankOfChangesValue;
  description?: string;
  color?: string;
  insights?: CrossCheckInsight[];
  submission_status?: ClauseSubmissionStatus;
  verification_status?: ClauseVerificationStatus;
  lock_status?: ClauseLockStatus;
  // Module B fields
  original_text?: string;
  edited_text?: string;
  track_changes?: any;
  participants?: string[];
  module_type?: 'module_a' | 'module_b';
  /** Optional paragraph index in the Word document (for replace/submit) */
  paragraph_index?: number;
  /** AI-generated interpretation of track changes (Module B); same as dashboard Explanation */
  track_changes_interpretation?: string;
  /** Original document type before a move (used to restore without re-analysis). */
  origin_document_type?: CrossCheckDocumentType;
  /** Optional Past Agreement matches (Construction Loan only) */
  past_agreement?: PastAgreementValue;
  /** Construction Loan only: matter classification used for UI tabs. */
  matter?: CrossCheckMatter;
}

export function mapRawClause(raw: RawCrossCheckClause): CrossCheckClause {
  const docType = (raw.documentType as CrossCheckDocumentType) || 'sales_agreement';
  const isAppendix = raw.appendix_a_bank_of_changes != null;

  const bankOfChanges = isAppendix
    ? raw.appendix_a_bank_of_changes
    : raw.sales_agreement_bank_of_changes;

  let clientRequest = raw.client_request;
  if (
    !clientRequest &&
    raw.module_type === 'module_b' &&
    (raw.track_changes || raw.original_text || raw.edited_text)
  ) {
    clientRequest = JSON.stringify({
      original_text: raw.original_text || '',
      edited_text: raw.edited_text || raw.text || '',
      track_changes: raw.track_changes || null,
      change_history: raw.change_history || null,
      participants: raw.participants || [],
      clause_number: raw.clause_number || '',
      refered_file: raw.refered_file || '',
      module_type: raw.module_type,
    }, null, 2);
  }

  let paragraphIndex: number | undefined;
  if (raw.paragraph_index != null) {
    paragraphIndex = Number(raw.paragraph_index);
  } else if (typeof raw.track_changes === 'object' && raw.track_changes != null && 'paragraph_index' in raw.track_changes) {
    const pi = (raw.track_changes as { paragraph_index?: number }).paragraph_index;
    if (pi != null) paragraphIndex = Number(pi);
  }
  if (paragraphIndex == null && raw.edit_history && typeof raw.edit_history === 'object') {
    for (const key of Object.keys(raw.edit_history)) {
      const arr = (raw.edit_history as Record<string, unknown>)[key];
      if (Array.isArray(arr) && arr.length > 0) {
        const last = arr[arr.length - 1] as { paragraph_index?: number };
        if (last?.paragraph_index != null) {
          paragraphIndex = Number(last.paragraph_index);
          break;
        }
      }
    }
  }
  if (paragraphIndex == null && raw.insights?.[0]?.chunk_metadata?.paragraph_index != null) {
    paragraphIndex = Number(raw.insights[0].chunk_metadata!.paragraph_index);
  }

  // Construction Loan classifies each request by `matter` (commercial / legal
  // / other). Older records or non-CL records may not include this field — we
  // pass it through verbatim and let the Word add-in default to "other" when
  // grouping by matter.
  const allowedMatters: ReadonlyArray<CrossCheckMatter> = ['commercial', 'legal', 'other'];
  const rawMatter = typeof raw.matter === 'string' ? raw.matter.trim().toLowerCase() : undefined;
  const matter: CrossCheckMatter | undefined =
    rawMatter && (allowedMatters as readonly string[]).includes(rawMatter)
      ? (rawMatter as CrossCheckMatter)
      : undefined;

  return {
    id: raw.id,
    clause_number: raw.clause_number,
    documentType: docType,
    sales_agreement: raw.sales_agreement,
    client_request: clientRequest,
    bank_of_changes: bankOfChanges,
    description: raw.description,
    color: raw.color,
    insights: raw.insights,
    submission_status: raw.submission_status,
    verification_status: raw.verification_status,
    lock_status: raw.lock_status,
    original_text: raw.original_text,
    edited_text: raw.edited_text,
    track_changes: raw.track_changes,
    participants: raw.participants,
    module_type: raw.module_type,
    paragraph_index: paragraphIndex,
    track_changes_interpretation: raw.track_changes_interpretation,
    past_agreement: (raw as any).past_agreement,
    matter,
    origin_document_type: raw.origin_document_type as CrossCheckDocumentType | undefined,
  };
}

export function getPrimaryBankOfChangesText(boc: BankOfChangesValue | undefined): string {
  if (!boc) return '';
  if (typeof boc === 'string') return boc;
  if ('chosen_option' in boc) return typeof boc.chosen_option === 'string' ? boc.chosen_option : String(boc.chosen_option || '');
  if (Array.isArray(boc) && boc.length > 0) return boc[0].text;
  return '';
}

export function getBocOptionCount(boc: BankOfChangesValue | undefined): number {
  if (!boc) return 0;
  if (typeof boc === 'string') return 1;
  if ('options' in boc && Array.isArray(boc.options)) return boc.options.length;
  if (Array.isArray(boc)) return boc.length;
  return 0;
}

/** Backend uses "לא נמצא" when clause number is unknown; show "?" in the UI instead. */
export function formatClauseNumberForDisplay(
  value: string | number | undefined | null
): string {
  if (value === undefined || value === null) return '';
  const s = String(value).trim();
  if (s === 'לא נמצא') return '?';
  return s;
}
